/**
 * ManagementOverviewPage — "Správa firmy", the active company's own
 * administration (access overhaul Stage 5). One TanStack route at
 * `/management` handles every tab:
 *   `?tab` absent / `prehled` — hub (nav cards + latest change history)
 *   `?tab=clenove`            — members & module grants
 *   `?tab=historie`           — membership change history (shared HistorieWorkspace)
 *   `?tab=ebm` / `ebm-report` — EBM (Ford) reporting
 *   `?tab=premier`            — Premier partner lookup
 *
 * Visibility: company admins (`membership.spravce`) and platform admins
 * (`user.admin`) — both see exactly the same tabs for the active company.
 * Platform administration (Firmy / Uživatelé / Záznamy úloh) lives at
 * `/platforma`; the old `?tab=` links redirect there. The module-owned admin
 * tools (ceníky, SPZ pairing, LMS, customer dedup) live at `/vozidla/sprava` +
 * `/zakaznici/sprava`.
 *
 * Shared chrome (Shell · AppRail · Crown · Subtabs) stays mounted across
 * tab switches; only the body block re-renders. Crown KPIs adapt to the
 * active tab.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Shell } from '@/components/Shell';
import { Subtabs } from '@/components/Subtabs';
import { useModuleAccess } from '@/lib/permissions';
import { OverviewBody } from './OverviewBody';
import { ClenoveWorkspace } from './ClenoveWorkspace';
import { EbmWorkspace } from './EbmWorkspace';
import { EbmReportWorkspace } from './EbmReportWorkspace';
import { ServisEmailWorkspace } from './ServisEmailWorkspace';
import { NabidkyWorkspace } from './NabidkyWorkspace';
import { PremierWorkspace } from './PremierWorkspace';
import { HistorieWorkspace } from '@/modules/historie/HistorieWorkspace';

import type { ManagementSearch, ManagementTab } from './helpers';

export type { ManagementSearch, ManagementTab };

const TAB_OPTIONS_BASE: { value: ManagementTab; label: string }[] = [
  { value: 'prehled', label: 'Přehled' },
];

/** Company-admin tabs — visible to spravce OR platform admin. */
const SPRAVCE_TAB_OPTIONS: { value: ManagementTab; label: string }[] = [
  { value: 'clenove', label: 'Členové' },
  { value: 'historie', label: 'Historie' },
];

/** EBM reporting — spravce-gated server-side since Stage 5. */
const EBM_TAB_OPTIONS: { value: ManagementTab; label: string }[] = [
  { value: 'ebm', label: 'EBM zařazení' },
  { value: 'ebm-report', label: 'EBM report' },
];

/** Premier (economy system) — spravce-gated server-side. */
const PREMIER_TAB_OPTIONS: { value: ManagementTab; label: string }[] = [
  { value: 'premier', label: 'Premier' },
];

/** Servis customer-email identity — spravce-gated server-side. */
const SERVIS_EMAIL_TAB_OPTIONS: { value: ManagementTab; label: string }[] = [
  { value: 'servis-email', label: 'Servis e-maily' },
];

/** Price-offer settings (validity) — spravce-gated server-side. */
const NABIDKY_TAB_OPTIONS: { value: ManagementTab; label: string }[] = [
  { value: 'nabidky', label: 'Nabídky' },
];

export function ManagementOverviewPage() {
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/management' }) as ManagementSearch;
  // Legacy platform tabs redirect in the route's beforeLoad; never render them.
  const activeTab: ManagementTab =
    urlSearch.tab === 'clenove' ||
    urlSearch.tab === 'historie' ||
    urlSearch.tab === 'ebm' ||
    urlSearch.tab === 'ebm-report' ||
    urlSearch.tab === 'premier' ||
    urlSearch.tab === 'servis-email' ||
    urlSearch.tab === 'nabidky'
      ? urlSearch.tab
      : 'prehled';

  const { isSpravce, isAdmin, resolved } = useModuleAccess();
  const canManage = isSpravce || isAdmin;
  // The Správa area is for company admins + platform admins; a deep link by
  // anyone else gets the explanation below (the rail item is already hidden).
  const accessDenied = resolved && !canManage;

  const onTabChange = (next: ManagementTab) => {
    if (next === activeTab) return;
    if (next === 'prehled') {
      navigate({ to: '/management', search: {}, replace: true });
    } else {
      navigate({ to: '/management', search: { tab: next }, replace: true });
    }
  };

  const tabOptions = [
    ...TAB_OPTIONS_BASE,
    ...(canManage ? SPRAVCE_TAB_OPTIONS : []),
    ...(canManage ? EBM_TAB_OPTIONS : []),
    ...(canManage ? PREMIER_TAB_OPTIONS : []),
    ...(canManage ? SERVIS_EMAIL_TAB_OPTIONS : []),
    ...(canManage ? NABIDKY_TAB_OPTIONS : []),
  ];

  const heading =
    activeTab === 'clenove' ? (
      <DisplayHeading>
        Členové,
        <br />
        <em>práva modulů.</em>
      </DisplayHeading>
    ) : activeTab === 'historie' ? (
      <DisplayHeading>
        Historie,
        <br />
        <em>kdo co změnil.</em>
      </DisplayHeading>
    ) : activeTab === 'ebm' ? (
      <DisplayHeading>
        EBM zařazení,
        <br />
        <em>účty a OZN.</em>
      </DisplayHeading>
    ) : activeTab === 'ebm-report' ? (
      <DisplayHeading>
        EBM report,
        <br />
        <em>náklady a výnosy.</em>
      </DisplayHeading>
    ) : activeTab === 'premier' ? (
      <DisplayHeading>
        Premier,
        <br />
        <em>partneři z účetnictví.</em>
      </DisplayHeading>
    ) : activeTab === 'servis-email' ? (
      <DisplayHeading>
        Servis e-maily,
        <br />
        <em>podpis a kontakt.</em>
      </DisplayHeading>
    ) : activeTab === 'nabidky' ? (
      <DisplayHeading>
        Nabídky,
        <br />
        <em>platnost.</em>
      </DisplayHeading>
    ) : (
      <DisplayHeading>
        Správa firmy,
        <br />
        <em>přehled.</em>
      </DisplayHeading>
    );

  return (
    <Shell rail={<AppRail active="management" />}>
      <Crown>
        <Breadcrumb
          segments={[
            { label: 'Správa firmy' },
            {
              label:
                activeTab === 'clenove'
                  ? 'Členové'
                  : activeTab === 'historie'
                    ? 'Historie'
                    : activeTab === 'ebm'
                      ? 'EBM zařazení'
                      : activeTab === 'ebm-report'
                        ? 'EBM report'
                        : activeTab === 'premier'
                          ? 'Premier'
                          : activeTab === 'servis-email'
                            ? 'Servis e-maily'
                            : activeTab === 'nabidky'
                              ? 'Nabídky'
                              : 'Přehled',
            },
          ]}
        />
        <Crown.Row className="mt-3">
          {heading}
          <CrownMeta
            items={[
              {
                label: 'Role',
                value: isAdmin ? 'Admin' : isSpravce ? 'Správce' : 'Uživatel',
              },
            ]}
          />
        </Crown.Row>
        <Subtabs<ManagementTab>
          aria-label="Správa firmy — sekce"
          value={activeTab}
          onChange={onTabChange}
          items={tabOptions}
        />
      </Crown>

      {accessDenied && activeTab === 'prehled' ? (
        <div
          data-testid="management-overview"
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
        >
          <EmptyState
            eyebrow="403"
            title="Správa firmy není dostupná"
            description="Sekce je určena správcům firmy. Pokud potřebujete přístup, požádejte svého správce."
          />
        </div>
      ) : activeTab === 'clenove' ? (
        <ClenoveWorkspace canManage={canManage} />
      ) : activeTab === 'historie' ? (
        canManage ? (
          <HistorieWorkspace moduly={['firma']} />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5">
            <EmptyState
              eyebrow="403"
              title="Pouze pro správce firmy"
              description="Historii změn vidí správci firmy a administrátoři."
            />
          </div>
        )
      ) : activeTab === 'ebm' ? (
        <EbmWorkspace canManage={canManage} />
      ) : activeTab === 'ebm-report' ? (
        <EbmReportWorkspace canManage={canManage} />
      ) : activeTab === 'premier' ? (
        <PremierWorkspace canManage={canManage} />
      ) : activeTab === 'servis-email' ? (
        <ServisEmailWorkspace canManage={canManage} />
      ) : activeTab === 'nabidky' ? (
        <NabidkyWorkspace canManage={canManage} />
      ) : (
        <OverviewBody canManage={canManage} />
      )}
    </Shell>
  );
}
